import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { PromptPayQR, usePromptPayConfig } from "../utils/promptpay";
import { useLanguage } from "../context/LanguageContext";
import { t } from "../i18n";
import Toast from "../components/Toast";
import SearchableSelect from "../components/SearchableSelect";
import API from "../config";
import {
  GUEST_TYPES, STAY_PURPOSES, TH_PROVINCES,
  parseCarProvince, suggestGuestType, guestTypeLabel, guestTypeColor,
} from "../constants/guest";
import { runIdCardOcr, terminateOcrWorker, FILLABLE_FIELDS } from "../utils/idCardOcr";

function AvatarWithFallback({ src, role, imgClass, phClass }) {
  const [imgError, setImgError] = useState(false);
  if (src && !imgError) {
    return <img className={imgClass} src={src} alt="" onError={() => setImgError(true)} />;
  }
  return (
    <div className={`${phClass} ${role}`}>
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ width: "55%", height: "55%" }}>
        <circle cx="12" cy="8" r="4" fill="currentColor" opacity="0.9"/>
        <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" fill="currentColor" opacity="0.9"/>
      </svg>
    </div>
  );
}

/* state ที่ agent รายงานผ่าน /status → ค่า agentStatus ในหน้าเว็บ
   ค่าเหล่านี้ถูกใช้เป็น CSS class ตรง ๆ ด้วย (.ci-agent-status.<state>) */
const AGENT_STATE_MAP = {
  CARD:           "card",
  READY:          "online",
  NO_READER:      "no-reader",
  NO_PCSC:        "no-pcsc",
  UNSUPPORTED_OS: "no-pcsc",
};

const AGENT_STATUS_LABEL = {
  checking:    "ciAgentChecking",
  online:      "ciAgentOnline",        // มีเครื่องอ่านต่ออยู่จริง แต่ยังไม่ได้เสียบบัตร
  card:        "ciAgentCardIn",        // เสียบบัตรแล้ว พร้อมกดอ่าน
  "no-reader": "ciAgentNoReader",
  "no-pcsc":   "ciAgentNoPcsc",
  offline:     "ciAgentOfflineShort",  // agent ไม่ตอบ (ยังกดปุ่มได้ — มี fallback ไป backend)
  error:       "ciAgentCheckFail",
};

/* ฟอร์มเช็คอินทั้งหมด — ทุก key ถูกส่งขึ้น /api/checkin/:id ตามชื่อนี้ตรงๆ */
const EMPTY_FORM = {
  fullName: "", address: "", birthDate: "", gender: "",
  country: "", nationality: "", phone: "", extra: "", idCard: "",
  // ช่องที่แอดมินกรอกเพิ่ม — บริษัท (ใช้ออกใบเสร็จ)
  companyName: "", companyAddress: "", companyBranchNo: "", companyTaxId: "",
  // ช่องที่แอดมินกรอกเพิ่ม — รถ (ไม่เข้าใบเสร็จ ใช้เป็นประวัติ + วิเคราะห์)
  carBrand: "", carColor: "", carPlate: "", carProvince: "",
  // dimension วิเคราะห์การขาย
  guestType: "", stayPurpose: "",
};

/* draft = การจองจากหน้าปฏิทินที่ติ๊ก "เช็คอินเลย" แต่ยังไม่ถูกบันทึกลงระบบ
   เก็บใน sessionStorage ด้วย เพราะ state ของ router หายเมื่อผู้ใช้กด refresh
   ล้างทิ้งเมื่อเช็คอินสำเร็จ หรือเจ้าหน้าที่ยืนยันว่าจะออกโดยไม่บันทึก */
const DRAFT_KEY = "checkinDraft";

function CheckIn() {
  const token = localStorage.getItem("token");
  const location = useLocation();
  const navigate = useNavigate();
  const autoSelectId = location.state?.bookingId ?? null;
  const { lang } = useLanguage(); // eslint-disable-line no-unused-vars
  const promptpayConfig = usePromptPayConfig();

  const [bookings,   setBookings]   = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [search,     setSearch]     = useState("");
  const [toasting,   setToasting]   = useState(null);
  const [errorMsg,   setErrorMsg]   = useState("");

  const [selected,   setSelected]   = useState(null);
  const [form,       setForm]       = useState({ ...EMPTY_FORM });

  /* ── ห้องที่จองมาพร้อมกัน (groupId เดียวกัน) ──
     เช็คอินทีเดียวทั้งกลุ่ม แต่ต้องกรอกผู้เข้าพักทีละห้อง — ห้องละคน
     group[gIdx] คือห้องที่กำลังกรอกอยู่ (= selected) ส่วนฟอร์ม/ยอดรับชำระของห้องอื่น
     พักไว้ใน gForms/gPays เพื่อให้กด "ห้องก่อนหน้า" กลับมาแก้ได้โดยข้อมูลไม่หาย */
  const [group,  setGroup]  = useState([]);
  const [gIdx,   setGIdx]   = useState(0);
  const [draft,  setDraft]  = useState(null);   // ไม่ null = กำลังกรอกการจองที่ยังไม่ถูกบันทึก
  const roomSeq = useRef(0);   // เพิ่มขึ้นทุกครั้งที่สลับห้อง — งาน async ที่ค้างอยู่จะได้รู้ว่าตกรุ่นแล้ว
  const [gForms, setGForms] = useState([]);
  const [gPays,  setGPays]  = useState([]);
  const formRef = useRef(form);                  // ค่าฟอร์มล่าสุด สำหรับงาน async ที่กินเวลาหลายวินาที
  useEffect(() => { formRef.current = form; }, [form]);
  const [submitting, setSubmitting] = useState(false);
  const [readingCard, setReadingCard] = useState(false);
  // ดู AGENT_STATUS_LABEL — "online" = มีเครื่องอ่านต่ออยู่จริง (ไม่ใช่แค่ agent รันอยู่)
  const [agentStatus, setAgentStatus] = useState("checking");

  /* OCR อ่านข้อมูลจากรูปบัตร — รูปอยู่ในหน่วยความจำเบราว์เซอร์ชั่วคราวเท่านั้น
     ไม่อัปโหลด ไม่บันทึกลงเซิร์ฟเวอร์ อ่านเสร็จก็ทิ้ง */
  const [ocrBusy, setOcrBusy] = useState(false);
  const [ocrPct,  setOcrPct]  = useState(0);
  const [ocrDone, setOcrDone] = useState(false);
  const ocrSeq = useRef(0);   // กันผลของรูปใบเก่ามาทับใบใหม่ถ้าแอดมินเลือกซ้อน

  /* ลูกค้าเดิมในทะเบียน (ถ้าเคยมาแล้ว) + ประวัติการเข้าพักครั้งก่อนๆ */
  const [guestMatch, setGuestMatch] = useState(null);
  const [lookingUp,  setLookingUp]  = useState(false);
  const lookupSeq = useRef(0);   // กันผลค้นของห้องก่อนหน้ามาทับตอนสลับห้องในกลุ่ม

  /* รับชำระยอดค้างตอนเช็คอิน */
  const [payment,   setPayment]   = useState(null);   // null | { method:"cash"|"qr", amount }
  const [showPay,   setShowPay]   = useState(false);
  const [payMethod, setPayMethod] = useState("cash");

  /* มุมมอง เข้าวันนี้ / ทั้งหมด */
  const [viewTab, setViewTab] = useState("all");

  /* จองด่วนหน้าเคาน์เตอร์ (Walk-in) */
  const [showWalkIn, setShowWalkIn] = useState(false);
  const [roomTypes,  setRoomTypes]  = useState([]);
  // โหลดเสร็จแล้วหรือยัง (สำเร็จหรือพลาดก็นับ) — draft ต้องรอรายละเอียดประเภทห้องก่อนเปิดฟอร์ม
  const [roomTypesReady, setRoomTypesReady] = useState(false);
  const todayStr    = new Date().toISOString().slice(0, 10);
  const tomorrowStr = new Date(Date.now() + 86400000).toISOString().slice(0, 10);
  const emptyWalkIn = { roomTypeId: "", checkIn: todayStr, checkOut: tomorrowStr, guestName: "", phone: "", payMethod: "cash", paid: "full" };
  const [wi, setWi]           = useState(emptyWalkIn);
  const [wiBusy, setWiBusy]   = useState(false);
  const [wiErr,  setWiErr]    = useState("");

  useEffect(() => {
    fetch(`${API}/api/rooms`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(d => { if (Array.isArray(d)) setRoomTypes(d); }).catch(() => {})
      .finally(() => setRoomTypesReady(true));
  }, [token]);

  const wiRoom   = roomTypes.find(r => r._id === wi.roomTypeId) || null;
  const wiNights = (() => {
    const a = new Date(wi.checkIn), b = new Date(wi.checkOut);
    return Math.max(0, Math.round((b - a) / 86400000));
  })();
  const wiTotal  = (Number(wiRoom?.price) || 0) * wiNights;

  async function submitWalkIn() {
    if (!wi.roomTypeId) { setWiErr(t(lang, "wiErrRoom")); return; }
    if (wiNights < 1)   { setWiErr(t(lang, "wiErrDate")); return; }
    setWiBusy(true); setWiErr("");
    try {
      const paidAmount = wi.paid === "full" ? wiTotal : 0;
      const res = await fetch(`${API}/api/admin/walkin-booking`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          roomTypeId: wi.roomTypeId,
          checkIn:    wi.checkIn,
          checkOut:   wi.checkOut,
          guestName:  wi.guestName,
          phone:      wi.phone,
          paidAmount,
          paymentMethod: wi.payMethod,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || t(lang, "ciToastCheckinFail"));
      toast(`${t(lang, "wiSuccess")} · ${t(lang, "ciRoomLabel")} ${data.roomNumber || ""}`);
      setShowWalkIn(false);
      setWi(emptyWalkIn);
      fetchBookings();
    } catch (err) {
      setWiErr(err.message);
    } finally {
      setWiBusy(false);
    }
  }

  const fetchBookings = useCallback(async () => {
    setLoading(true); setErrorMsg("");
    try {
      const res  = await fetch(`${API}/api/bookings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) { setErrorMsg(data?.message || `HTTP ${res.status}`); setBookings([]); return; }
      if (!Array.isArray(data)) { setErrorMsg(t(lang, "ciErrBadData")); setBookings([]); return; }
      setBookings(
        data
          .filter(b => !b.isBlock && b.status === "Approved")
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
      );
    } catch (err) { setErrorMsg(`${t(lang, "ciErrConnect")}: ${err.message}`); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchBookings(); }, [fetchBookings]);

  useEffect(() => {
    if (autoSelectId && bookings.length > 0) {
      const found = bookings.find(b => b._id === autoSelectId);
      if (found) openForm(groupOf(found));
    }
  }, [autoSelectId, bookings]);

  /* ── รับ draft จากหน้าปฏิทิน (ติ๊ก "เช็คอินเลย") ──
     ยังไม่มีใบจองในระบบ ณ จุดนี้ — จะถูกสร้างพร้อมเช็คอินตอนกดยืนยันเท่านั้น */
  useEffect(() => {
    const incoming = location.state?.draft;
    if (incoming) {
      try { sessionStorage.setItem(DRAFT_KEY, JSON.stringify(incoming)); } catch {}
      setDraft(incoming);
      return;
    }
    // กด refresh แล้ว state ของ router หาย — หยิบของที่พักไว้กลับมา
    try {
      const saved = sessionStorage.getItem(DRAFT_KEY);
      if (saved) setDraft(JSON.parse(saved));
    } catch {}
  }, [location.state]);

  const draftOpened = useRef(false);
  useEffect(() => {
    if (!draft || !roomTypesReady || draftOpened.current) return;
    // draft ที่พังหรือไม่มีห้อง (เช่นค้างอยู่ใน sessionStorage จากรุ่นก่อน) — ทิ้งไปเงียบๆ
    if (!Array.isArray(draft.rooms) || draft.rooms.length === 0) { clearDraft(); return; }
    draftOpened.current = true;
    openForm(draftBookings(draft));
  }, [draft, roomTypesReady]);   // eslint-disable-line react-hooks/exhaustive-deps

  /* แปลง draft เป็นข้อมูลหน้าตาเหมือนใบจอง เพื่อใช้ฟอร์ม/ตัวเดินห้องชุดเดียวกับเช็คอินปกติ
     ยอดที่แสดงคิดด้วยสูตรเดียวกับ backend (คืน × ราคาห้อง, กระจายยอดชำระตามสัดส่วน)
     แต่ยอดที่บันทึกจริงคือยอดที่ backend คิดเองตอนสร้างใบจอง */
  function draftBookings(d) {
    const nightsOf = (a, b) => Math.max(1, Math.round((new Date(b) - new Date(a)) / 86400000));
    const totals = d.rooms.map(r => {
      const rt = roomTypes.find(x => String(x._id) === String(r.roomTypeId));
      return (Number(rt?.price ?? r.price) || 0) * nightsOf(r.checkIn, r.checkOut);
    });
    const grand = totals.reduce((a, b) => a + b, 0);
    const paid  = Math.min(Math.max(0, Number(d.paidAmount) || 0), grand);
    const paidPer = totals.map(tt => (grand > 0 ? Math.floor(paid * tt / grand) : 0));
    if (paidPer.length) paidPer[0] += paid - paidPer.reduce((a, b) => a + b, 0);

    return d.rooms.map((r, i) => ({
      _id:         `draft-${r.unitId}`,   // ใช้เป็น key ใน React เท่านั้น ไม่ใช่ id จริง
      isDraft:     true,
      roomUnit:    { _id: r.unitId, roomNumber: r.roomNumber },
      roomType:    roomTypes.find(x => String(x._id) === String(r.roomTypeId))
                   || { name: "", price: r.price },
      checkIn:     r.checkIn,
      checkOut:    r.checkOut,
      guestName:   r.guestName,
      draftPhone:  r.phone,
      totalAmount: totals[i],
      paidAmount:  paidPer[i],
      createdAt:   null,
    }));
  }

  function clearDraft() {
    try { sessionStorage.removeItem(DRAFT_KEY); } catch {}
    draftOpened.current = false;
    setDraft(null);
  }

  function toast(msg, type = "success") {
    setToasting({ msg, type });
    setTimeout(() => setToasting(null), 2800);
  }

  function getGuestName(b) {
    return `${b.user?.firstname ?? ""} ${b.user?.lastname ?? ""}`.trim() || b.guestName || t(lang, "ciUnknownName");
  }

  function getAvatarRole(user) {
    return (user?.role === "admin" || user?.role === "staff") ? "staff" : "guest";
  }

  /* ห้องแรกใช้ชื่อ/เบอร์ของผู้จองเป็นค่าตั้งต้น
     ห้องถัดไปเป็นคนละคน — ปล่อยว่างไว้ ไม่งั้นชื่อผู้จองจะถูกบันทึกซ้ำทุกห้องโดยไม่ตั้งใจ
     (ถ้าเป็นคนกลุ่มเดียวกันจริง กดปุ่มคัดลอกข้อมูลจากห้องก่อนหน้าได้) */
  function blankForm(booking, isFirst) {
    // จองจากปฏิทินกรอกชื่อ/เบอร์แยกรายห้องมาแล้ว — ใช้ของห้องนั้นเป็นค่าตั้งต้นได้เลยทุกห้อง
    if (booking?.isDraft) {
      return { ...EMPTY_FORM, fullName: booking.guestName || "", phone: booking.draftPhone || "" };
    }
    return {
      ...EMPTY_FORM,
      fullName: isFirst ? getGuestName(booking) : "",
      phone:    isFirst ? (booking.user?.phone ?? "") : "",
    };
  }

  /* ใบจองที่จองมาพร้อมกัน เรียงตามลำดับตอนจอง — ห้องที่เช็คอินไปแล้วไม่อยู่ใน bookings อยู่แล้ว */
  function groupOf(booking) {
    if (!booking?.groupId) return [booking];
    return bookings
      .filter(b => String(b.groupId) === String(booking.groupId))
      .sort((a, b) => (a.groupIndex ?? 0) - (b.groupIndex ?? 0));
  }

  function openForm(bookingOrGroup) {
    const list  = Array.isArray(bookingOrGroup) ? bookingOrGroup : [bookingOrGroup];
    const forms = list.map((b, i) => blankForm(b, i === 0));
    ocrSeq.current++; lookupSeq.current++; roomSeq.current++;   // งานค้างของใบจองก่อนหน้าต้องไม่ตามมา
    setGroup(list);
    setGForms(forms);
    setGPays(list.map(() => null));
    setGIdx(0);
    setForm(forms[0]);
    setOcrDone(false);
    setOcrPct(0);
    setGuestMatch(null);
    setPayment(null);
    setShowPay(false);
    setPayMethod("cash");
    setSelected(list[0]);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  /* สลับไปกรอกห้องอื่นในกลุ่ม — เก็บฟอร์ม/ยอดรับชำระของห้องปัจจุบันไว้ก่อนเสมอ */
  function goToRoom(idx) {
    if (idx < 0 || idx >= group.length || idx === gIdx) return;
    const forms = [...gForms]; forms[gIdx] = form;
    const pays  = [...gPays];  pays[gIdx]  = payment;
    setGForms(forms);
    setGPays(pays);
    setForm(forms[idx] ?? { ...EMPTY_FORM });
    setPayment(pays[idx] ?? null);
    setSelected(group[idx]);
    setGIdx(idx);
    /* งานที่ค้างอยู่เป็นของห้องเดิมทั้งหมด — ผล OCR ที่กำลังวิ่งอยู่ต้องไม่ตกมาที่ห้องใหม่
       และแบนเนอร์ลูกค้าเก่า/สถานะ OCR ของห้องเดิมต้องไม่ค้างให้เข้าใจผิด */
    ocrSeq.current++;
    lookupSeq.current++;
    roomSeq.current++;
    setOcrBusy(false);
    setOcrDone(false);
    setOcrPct(0);
    setLookingUp(false);
    setGuestMatch(null);
    setShowPay(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function closeForm() {
    setSelected(null);
    setGroup([]);
    setGForms([]);
    setGPays([]);
    setGIdx(0);
  }

  /* ปุ่มกลับ — โหมด draft ยังไม่มีอะไรถูกบันทึก ออกไปแล้วการจองจะหายทั้งใบ ต้องถามก่อน */
  function handleBack() {
    if (draft) {
      if (!window.confirm(t(lang, "ciDraftLeaveConfirm"))) return;
      clearDraft();
      closeForm();
      navigate(-1);
      return;
    }
    closeForm();
  }

  /* ── ค้นทะเบียนลูกค้า → เติมช่องที่ยังว่างให้ + ดึงประวัติการเข้าพักมาแสดง ──
     เรียกหลังอ่านบัตรสำเร็จ และตอนพิมพ์เลขบัตรเองแล้วออกจากช่อง
     เขียนทับเฉพาะช่องที่ว่าง — สิ่งที่แอดมินเพิ่งกรอก/อ่านจากบัตรต้องไม่หาย */
  const lookupGuest = useCallback(async ({ idCard, phone, fullName }) => {
    const id = String(idCard || "").trim();
    if (!id && !(phone && fullName)) return;
    // กันผลของห้อง/เลขบัตรก่อนหน้ามาเติมทับฟอร์มของห้องที่สลับมาใหม่
    const seq = ++lookupSeq.current;
    setLookingUp(true);
    try {
      const qs = new URLSearchParams();
      if (id) qs.set("idCard", id);
      else { qs.set("phone", phone); qs.set("fullName", fullName); }

      const res = await fetch(`${API}/api/guests/lookup?${qs}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const data = await res.json();
      if (seq !== lookupSeq.current) return;   // สลับห้องไปแล้ว ทิ้งผลนี้
      if (!data.found) { setGuestMatch(null); return; }

      setGuestMatch(data);
      const g = data.guest || {};
      setForm(f => {
        const next = { ...f };
        for (const k of Object.keys(EMPTY_FORM)) {
          if (!String(next[k] || "").trim() && g[k]) next[k] = g[k];
        }
        return next;
      });
      toast(`${t(lang, "ciReturningGuest")} · ${t(lang, "ciStayCount", data.stayCount)}`);
    } catch {
      /* ค้นไม่เจอไม่ใช่ความผิดพลาด — เช็คอินต่อได้ตามปกติ */
    } finally {
      if (seq === lookupSeq.current) setLookingUp(false);
    }
  }, [token, lang]);

  // Local agent บนเครื่องหน้าเคาน์เตอร์ (ดู /idcard-agent) — ใช้ได้แม้ backend เป็น Linux
  const IDCARD_AGENT_URL = process.env.REACT_APP_IDCARD_AGENT_URL || "http://127.0.0.1:5151/read";

  // /status เช็ค PC/SC จริง (agent cache ผลไว้ 3 วิ) — ต่างจาก /ping ที่บอกแค่ว่าโปรเซส agent รันอยู่
  const checkAgent = useCallback(async () => {
    setAgentStatus("checking");
    try {
      const statusUrl = IDCARD_AGENT_URL.replace(/\/read\/?$/, "/status");
      const res = await fetch(statusUrl, { signal: AbortSignal.timeout(3000) });
      // agent รุ่นเก่ายังไม่มี /status — อย่าให้เครื่องที่ยังไม่อัปเดตขึ้นแดง
      if (res.status === 404) return setAgentStatus("online");
      if (!res.ok) return setAgentStatus("offline");
      const data = await res.json();
      // BUSY = agent กำลังอ่านบัตรอยู่ ยังไม่รู้สถานะใหม่ → คงค่าเดิมไว้
      if (data?.state === "BUSY") return setAgentStatus(prev => (prev === "checking" ? "online" : prev));
      setAgentStatus(AGENT_STATE_MAP[data?.state] || "error");
    } catch {
      setAgentStatus("offline");
    }
  }, [IDCARD_AGENT_URL]);

  // ตรวจสถานะ agent เมื่อเปิดฟอร์มเช็คอิน แล้ว poll ซ้ำทุก 5 วิ (รีเฟรชเอง)
  useEffect(() => {
    if (!selected || readingCard) return;
    checkAgent();
    const id = setInterval(checkAgent, 5000);
    return () => clearInterval(id);
  }, [selected, readingCard, checkAgent]);

  async function readIdCard() {
    if (readingCard) return;
    // อ่านบัตรรอได้ถึง 22 วิ — ถ้าแอดมินสลับไปห้องอื่นระหว่างนั้น ผลต้องไม่ตกใส่ห้องใหม่
    const seq = roomSeq.current;
    setReadingCard(true);
    try {
      // 1) ลองเรียก local agent ก่อน (เครื่องอ่านอยู่ที่เครื่องนี้)
      // 2) ถ้า agent ไม่ได้รัน → fallback ไป backend (กรณี backend รันบนเครื่อง Windows เดียวกัน)
      let res, usedFallback = false;
      try {
        res = await fetch(IDCARD_AGENT_URL, { signal: AbortSignal.timeout(22000) });
      } catch (agentDown) {
        usedFallback = true;
        setAgentStatus("offline");
        try {
          res = await fetch(`${API}/api/idcard/read`, {
            headers: { Authorization: `Bearer ${token}` },
            signal: AbortSignal.timeout(22000),
          });
        } catch (backendDown) {
          throw new Error(t(lang, "ciAgentOffline"));
        }
      }
      const data = await res.json();
      if (!res.ok) {
        // error จาก agent บอกสถานะฮาร์ดแวร์จริง — อัปเดตป้ายทันที ไม่ต้องรอ poll รอบถัดไป
        if (!usedFallback) {
          const byCode = { NO_READER: "no-reader", PCSC_UNAVAILABLE: "no-pcsc", UNSUPPORTED_OS: "no-pcsc", NO_CARD: "online" };
          if (byCode[data?.code]) setAgentStatus(byCode[data.code]);
        }
        // fallback ไป backend แล้ว แต่เครื่องนั้นไม่ได้ต่อเครื่องอ่าน → แนะนำให้เปิด agent
        if (usedFallback && ["UNSUPPORTED_OS", "PCSC_UNAVAILABLE", "NO_READER"].includes(data?.code)) {
          throw new Error(t(lang, "ciAgentOffline"));
        }
        throw new Error(data?.message || t(lang, "ciToastCheckinFail"));
      }

      if (seq !== roomSeq.current) return;   // สลับห้องไปแล้ว ทิ้งผลนี้

      const genderMap = { "1": "ชาย", "2": "หญิง" };
      setForm(f => ({
        ...f,
        idCard:      data.cid       || f.idCard,
        fullName:    data.nameTH    || f.fullName,
        birthDate:   data.birthDate || f.birthDate,
        gender:      genderMap[data.gender] || f.gender,
        address:     data.address   || f.address,
        nationality: f.nationality || "ไทย",
        country:     f.country || "ไทย",
      }));
      toast(t(lang, "ciReadCardOk"));
      // เคยมาพักแล้วหรือยัง — ดึงข้อมูลเดิม + ประวัติมาให้เลย
      if (data.cid) lookupGuest({ idCard: data.cid });
    } catch (err) {
      toast(err.message, "error");
    } finally {
      setReadingCard(false);   // effect ด้านบนจะ checkAgent() ให้เอง — บัตรอาจถูกดึงออกไปแล้ว
    }
  }

  /* ช่องอื่นที่ OCR เติมได้ — เติมเฉพาะตอนยังว่างเท่านั้น (ดูเหตุผลใน runOcrOnFile) */
  const OCR_SOFT_FIELDS = ["fullName", "birthDate", "gender", "address", "nationality", "country"];

  /* ── อ่านข้อมูลจากรูปบัตรด้วย OCR แล้วกรอกลงฟอร์มให้ ──
     รูปถูกใช้เฉพาะในเบราว์เซอร์ ไม่ถูกอัปโหลดและไม่ถูกบันทึกไว้ที่ใด

     กติกาการเขียนทับต่างจากเครื่องอ่านบัตร เพราะความน่าเชื่อถือต่างกัน:
     - เลขบัตร ผ่าน checksum มาแล้ว เชื่อถือได้ → เขียนทับได้เลย
     - ช่องอื่นเป็นข้อความที่ OCR อาจอ่านเพี้ยน และ openForm เติมชื่อจากใบจองไว้ก่อนแล้ว
       ถ้าปล่อยให้ทับ ชื่อจริงจากใบจองจะถูกแทนด้วยชื่อที่อ่านเพี้ยนโดยแอดมินไม่ทันสังเกต
       → เติมเฉพาะช่องที่ยังว่าง */
  async function runOcrOnFile(file) {
    const seq = ++ocrSeq.current;
    setOcrBusy(true);
    setOcrDone(false);
    setOcrPct(0);
    try {
      const { fields, text } = await runIdCardOcr(file, {
        onProgress: (pct) => { if (seq === ocrSeq.current) setOcrPct(pct); },
      });
      if (seq !== ocrSeq.current) return;   // มีรูปใบใหม่มาแล้ว ทิ้งผลใบนี้

      // ใช้ค่าล่าสุดจริงๆ — OCR ใช้เวลา 2-3 วิ แอดมินอาจพิมพ์อะไรไประหว่างนั้น
      const current = formRef.current;
      const merged  = { ...current };
      if (fields.idCard) merged.idCard = fields.idCard;
      OCR_SOFT_FIELDS.forEach((k) => { if (!merged[k] && fields[k]) merged[k] = fields[k]; });

      const filled = FILLABLE_FIELDS.filter((k) => merged[k] !== current[k]).length;
      if (!filled) {
        /* แยกให้ออกว่า "อ่านตัวหนังสือไม่ออกเลย" (รูปเบลอ/มืด/เล็กไป) กับ
           "อ่านออกแต่ไม่ตรงรูปแบบบัตร" (parser จับไม่ได้) — สองอย่างนี้แก้คนละทาง
           ข้อความดิบ log ไว้ให้ดูได้ว่า OCR เห็นอะไรจริง ๆ ตอนไล่ปัญหา */
        const readable = text.replace(/\s/g, "").length;
        console.warn(
          `[OCR] อ่านได้ ${readable} ตัวอักษร แต่กรอกไม่ได้สักช่อง\n` +
          `───── ข้อความดิบจาก OCR ─────\n${text || "(ว่างเปล่า)"}`
        );
        toast(t(lang, readable < 20 ? "ciOcrUnreadable" : "ciOcrNone"), "error");
        return;
      }

      setForm(merged);
      setOcrDone(true);
      toast(t(lang, "ciOcrOk", filled));
      // เคยมาพักแล้วหรือยัง — ดึงข้อมูลเดิม + ประวัติมาให้เลย
      if (fields.idCard) lookupGuest({ idCard: fields.idCard });
    } catch (err) {
      // OCR ล้มเหลวต้องไม่กระทบการเช็คอิน — แอดมินกรอกมือต่อได้ตามปกติ
      // แต่ต้อง log ของจริงไว้ ไม่งั้นแยกไม่ออกว่าโหลด worker ไม่ได้ หรือรูปพัง
      console.error("[OCR] อ่านบัตรไม่สำเร็จ:", err);
      if (seq === ocrSeq.current) toast(t(lang, "ciOcrFail"), "error");
    } finally {
      if (seq === ocrSeq.current) setOcrBusy(false);
    }
  }

  /* worker ของ OCR กิน memory พอสมควร — คืนทิ้งเมื่อออกจากหน้านี้ */
  useEffect(() => () => { terminateOcrWorker(); }, []);

  /* ประเภทผู้เข้าพักที่จะบันทึกจริง
     form.guestType ว่าง = แอดมินยังไม่ได้เลือกเอง → ใช้ค่าที่ระบบเดาจากข้อมูลที่กรอก */
  const autoGuestType      = suggestGuestType(form);
  const effectiveGuestType = form.guestType || autoGuestType;

  /* ข้อมูลที่คนกลุ่มเดียวกันมักใช้ร่วมกัน — คัดลอกข้ามห้องได้
     ไม่รวมชื่อ/เลขบัตร/วันเกิด/เพศ เพราะเป็นของแต่ละคน ลอกมาแล้วผิดทั้งใบ */
  const SHARED_FIELDS = [
    "address", "country", "nationality", "phone", "extra",
    "companyName", "companyAddress", "companyBranchNo", "companyTaxId",
    "carBrand", "carColor", "carPlate", "carProvince",
    "guestType", "stayPurpose",
  ];

  function copyFromPrevRoom() {
    const prev = gIdx > 0 ? gForms[gIdx - 1] : null;
    if (!prev) return;
    setForm(f => {
      const next = { ...f };
      SHARED_FIELDS.forEach(k => { if (prev[k]) next[k] = prev[k]; });
      return next;
    });
    toast(t(lang, "ciCopiedPrev"));
  }

  /* ส่งเช็คอินของห้องเดียว — คืน true/false ไม่โยน error ออกมา
     เพราะเช็คอินเป็นกลุ่มต้องยิงต่อให้ครบทุกห้อง แม้ห้องใดห้องหนึ่งพลาด */
  async function submitOne(booking, f, pay) {
    try {
      const fd = new FormData();
      // ฟิลด์ข้อความทั้งหมด — ไม่มีการแนบรูปบัตรอีกต่อไป (ใช้ OCR อ่านแล้วทิ้ง)
      Object.entries(f).forEach(([k, v]) => {
        fd.append(k, v ?? "");
      });
      // ประเภทผู้เข้าพัก: แอดมินไม่เลือกก็ใช้ค่าที่ระบบแนะนำ ไม่ปล่อยให้เป็น unknown
      fd.set("guestType", f.guestType || suggestGuestType(f));
      fd.set("guestTypeSource", f.guestType ? "manual" : "auto");
      fd.append("paymentReceived", pay?.amount || 0);
      fd.append("paymentMethod", pay?.method || "");

      const res = await fetch(`${API}/api/checkin/${booking._id}`, {
        method: "PUT",
        // อย่าตั้ง Content-Type เอง — ให้ browser ใส่ multipart boundary ให้อัตโนมัติ
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      return res.ok;
    } catch {
      return false;
    }
  }

  /* ── โหมด draft: สร้างใบจอง + เช็คอิน ในครั้งเดียว ──
     ยิงครั้งเดียวทั้งกลุ่ม ฝั่ง backend ทำครบทุกห้องหรือไม่บันทึกอะไรเลย
     พลาดเมื่อไหร่ = ไม่มีใบจองเกิดขึ้น ฟอร์มยังอยู่ครบให้กดซ้ำได้ */
  async function submitDraft(forms, pays) {
    const collected = pays.reduce((s, p) => s + (p?.amount || 0), 0);
    const method    = pays.find(p => p?.method)?.method || "cash";

    const res = await fetch(`${API}/api/admin/checkin-booking`, {
      method:  "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({
        rooms: draft.rooms.map(r => ({
          unitId:    r.unitId,
          checkIn:   r.checkIn,
          checkOut:  r.checkOut,
          guestName: r.guestName,
          phone:     r.phone,
        })),
        guestName:     draft.guestName,
        phone:         draft.phone,
        paidAmount:    (Number(draft.paidAmount) || 0) + collected,
        paymentMethod: method,
        guests: draft.rooms.map((_, i) => {
          const f = forms[i] ?? { ...EMPTY_FORM };
          return {
            ...f,
            guestType:       f.guestType || suggestGuestType(f),
            guestTypeSource: f.guestType ? "manual" : "auto",
          };
        }),
      }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      toast(data.message || t(lang, "ciDraftFail"), "error");
      return;
    }
    clearDraft();
    // ล้าง state ของ router ด้วย ไม่งั้นกดปุ่ม back/forward ของเบราว์เซอร์แล้ว draft เดิมจะกลับมา
    navigate(location.pathname, { replace: true, state: null });
    toast(data.message || t(lang, "ciToastCheckinOk"));
    closeForm();
    fetchBookings();
  }

  async function handleCheckIn() {
    if (!selected || group.length === 0) return;
    setSubmitting(true);
    try {
      // ห้องที่กำลังกรอกอยู่ยังไม่ถูกเก็บลง array (เก็บตอนกดสลับห้องเท่านั้น)
      const forms = [...gForms]; forms[gIdx] = form;
      const pays  = [...gPays];  pays[gIdx]  = payment;

      if (draft) {
        try {
          await submitDraft(forms, pays);
        } catch {
          // ยิงไม่ถึงเซิร์ฟเวอร์ = ไม่มีอะไรถูกสร้าง ฟอร์มค้างไว้ให้กดใหม่
          toast(t(lang, "ciDraftFail"), "error");
        }
        return;
      }

      const failed = [];
      for (let i = 0; i < group.length; i++) {
        const ok = await submitOne(group[i], forms[i] ?? { ...EMPTY_FORM }, pays[i]);
        if (!ok) failed.push(group[i]);
      }

      if (failed.length === group.length) {
        toast(t(lang, "ciToastCheckinFail"), "error");
        return;   // ไม่มีห้องไหนสำเร็จ — คงฟอร์มไว้ให้กดยืนยันซ้ำได้เลย
      }
      if (failed.length > 0) {
        // สำเร็จบางห้อง — ห้องที่พลาดยังอยู่ในรายการรอเช็คอิน กดเช็คอินซ้ำได้
        const rooms = failed.map(b => b.roomUnit?.roomNumber ?? "—").join(", ");
        toast(`${t(lang, "ciGroupPartialFail", failed.length)} (${t(lang, "ciRoomLabel")} ${rooms})`, "error");
      } else if (group.length > 1) {
        toast(t(lang, "ciGroupCheckedIn", group.length));
      } else {
        toast(`${t(lang, "ciToastCheckinOk")} ${getGuestName(selected)}`);
      }
      closeForm();
      fetchBookings();
    } finally {
      setSubmitting(false);
    }
  }

  const isSameDay = (d, ref) => {
    if (!d) return false;
    const x = new Date(d);
    return x.getFullYear() === ref.getFullYear() && x.getMonth() === ref.getMonth() && x.getDate() === ref.getDate();
  };
  const todayRef   = new Date();
  const todayCount = bookings.filter(b => isSameDay(b.checkIn, todayRef)).length;

  /* จองพร้อมกันหลายห้อง = หลายใบจองที่ผูกด้วย groupId เดียวกัน
     รวมเป็นการ์ดเดียวในรายการ เพราะกดเช็คอินครั้งเดียวแล้วไล่กรอกทีละห้องจนครบ */
  const groups = useMemo(() => {
    const seen = new Set();
    const out  = [];
    for (const b of bookings) {
      const gid = b.groupId ? String(b.groupId) : null;
      if (!gid) { out.push([b]); continue; }
      if (seen.has(gid)) continue;
      seen.add(gid);
      out.push(
        bookings
          .filter(x => String(x.groupId) === gid)
          .sort((p, q) => (p.groupIndex ?? 0) - (q.groupIndex ?? 0))
      );
    }
    return out;
  }, [bookings]);

  /* ค้น/กรองที่ระดับใบจอง แต่แสดงทั้งกลุ่ม — ค้นเลขห้องที่สองของกลุ่มก็ต้องเจอ */
  const matches = b => {
    if (viewTab === "today" && !isSameDay(b.checkIn, todayRef)) return false;
    const q = search.toLowerCase().trim();
    if (!q) return true;
    const name  = getGuestName(b).toLowerCase();
    const email = (b.user?.email ?? "").toLowerCase();
    const room  = (b.roomUnit?.roomNumber ?? "").toLowerCase();
    return name.includes(q) || email.includes(q) || room.includes(q);
  };
  const filtered = groups.filter(g => g.some(matches));

  const fmtDate = d => d
    ? new Date(d).toLocaleDateString("th-TH", {
        timeZone: "Asia/Bangkok",
        day: "numeric", month: "short", year: "numeric"
      })
    : "—";

  const fmtDateTime = d => d
    ? new Date(d).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })
    : "—";

  const fmtMoney = n => Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const toAvatarSrc = (av) => !av ? null : av.startsWith('http') ? av : `/uploads/${av}`;
  const avatarSrc = toAvatarSrc(selected?.user?.avatar);

  const total   = selected ? Number(selected.totalAmount ?? 0) : 0;
  const paid    = (selected ? Number(selected.paidAmount ?? 0) : 0) + (payment?.amount || 0);
  const balance = total - paid;
  // ยอดจองทั้งกลุ่ม — ยอดด้านล่างเป็นของห้องนี้ห้องเดียว (ตอนจองระบบหารยอดลงแต่ละห้องไว้แล้ว)
  const groupTotal = group.reduce((s, b) => s + Number(b.totalAmount ?? 0), 0);

  return (
    <>
      <style>{`
        .ci-page { min-height:100vh; background:#f5f5f5; font-family:'Noto Sans Thai',sans-serif; }
        .ci-wrap { max-width:1100px; margin:0 auto; padding:32px 24px 60px; }

        .ci-header { display:flex; align-items:center; justify-content:space-between; margin-bottom:24px; flex-wrap:wrap; gap:12px; }
        .ci-title  { display:flex; align-items:center; gap:8px; font-size:20px; font-weight:700; color:#1a1a1a; }
        .ci-search-wrap { position:relative; }
        .ci-search { padding:9px 14px 9px 36px; border-radius:8px; border:1px solid #e0e0e0; font-size:13px; width:240px; outline:none; background:#fff; }
        .ci-search::placeholder { color:#bbb; }
        .ci-search:focus { border-color:#aaa; }
        .ci-search-icon { position:absolute; left:10px; top:50%; transform:translateY(-50%); color:#bbb; pointer-events:none; }

        /* Today / All toggle */
        .ci-tabs { display:inline-flex; background:#eef0f2; border-radius:9px; padding:3px; gap:2px; }
        .ci-tab { border:none; background:none; padding:7px 14px; border-radius:7px; font-size:12.5px; font-weight:600; color:#6b7280; cursor:pointer; font-family:'Noto Sans Thai',sans-serif; white-space:nowrap; transition:background .15s,color .15s; }
        .ci-tab.active { background:#fff; color:#1a1a1a; box-shadow:0 1px 3px rgba(0,0,0,.1); }

        /* Walk-in button */
        .ci-walkin-btn { display:inline-flex; align-items:center; gap:6px; padding:9px 16px; background:#1d4ed8; color:#fff; border:none; border-radius:9px; font-size:13.5px; font-weight:600; cursor:pointer; font-family:'Noto Sans Thai',sans-serif; white-space:nowrap; transition:background .15s; }
        .ci-walkin-btn:hover { background:#1e40af; }

        /* Walk-in modal */
        .wi-overlay { position:fixed; inset:0; background:rgba(0,0,0,.45); z-index:2000; display:flex; align-items:center; justify-content:center; padding:16px; animation:ci-fadein .15s ease; }
        .wi-modal { background:#fff; border-radius:18px; width:100%; max-width:460px; max-height:92vh; overflow-y:auto; padding:24px 26px; box-shadow:0 12px 40px rgba(0,0,0,.2); }
        .wi-head { display:flex; justify-content:space-between; align-items:flex-start; margin-bottom:16px; }
        .wi-title { font-size:17px; font-weight:700; color:#1a1a1a; display:flex; align-items:center; gap:8px; }
        .wi-sub { font-size:12.5px; color:#6b7280; margin-top:3px; }
        .wi-close { background:none; border:none; font-size:18px; color:#9ca3af; cursor:pointer; padding:2px 6px; }
        .wi-field { display:flex; flex-direction:column; gap:5px; margin-bottom:13px; }
        .wi-field label { font-size:12.5px; font-weight:600; color:#374151; }
        .wi-field input, .wi-field select { padding:10px 13px; border-radius:9px; border:1px solid #e0e0e0; font-size:13.5px; font-family:'Noto Sans Thai',sans-serif; background:#fff; color:#1a1a1a; outline:none; box-sizing:border-box; }
        .wi-field input:focus, .wi-field select:focus { border-color:#1d4ed8; }
        .wi-row { display:grid; grid-template-columns:1fr 1fr; gap:12px; }
        .wi-total-box { background:#f9fafb; border:1px solid #eceff2; border-radius:12px; padding:13px 16px; display:flex; justify-content:space-between; align-items:center; margin:4px 0 14px; }
        .wi-total-box .lbl { font-size:12.5px; color:#6b7280; }
        .wi-total-box .val { font-size:20px; font-weight:800; color:#1a1a1a; }
        .wi-total-box .nights { font-size:11.5px; color:#9ca3af; }
        .wi-seg { display:flex; gap:8px; }
        .wi-seg button { flex:1; padding:9px; border-radius:9px; border:2px solid #e5e7eb; background:#fff; font-size:13px; font-weight:600; color:#374151; cursor:pointer; font-family:'Noto Sans Thai',sans-serif; }
        .wi-seg button.active { border-color:#16a34a; background:#f0fdf4; color:#166534; }
        .wi-err { background:#fef2f2; border:1px solid #fecaca; color:#991b1b; border-radius:9px; padding:9px 12px; font-size:12.5px; margin-bottom:12px; }
        .wi-actions { display:flex; gap:10px; margin-top:6px; }
        .wi-btn-cancel { flex:1; padding:12px; border-radius:999px; border:1px solid #d1d5db; background:#fff; color:#374151; font-size:14px; font-weight:600; cursor:pointer; font-family:'Noto Sans Thai',sans-serif; }
        .wi-btn-ok { flex:1.5; padding:12px; border-radius:999px; border:none; background:#1d4ed8; color:#fff; font-size:14px; font-weight:700; cursor:pointer; font-family:'Noto Sans Thai',sans-serif; }
        .wi-btn-ok:disabled { background:#9ca3af; cursor:not-allowed; }

        .ci-list  { display:flex; flex-direction:column; gap:12px; }
        .ci-card  { display:flex; align-items:center; gap:18px; padding:18px 24px; background:#fff; border:1px solid #e0e0e0; border-radius:14px; transition:border-color .15s; }
        .ci-card:hover { border-color:#c0c0c0; }
        .ci-avatar { width:88px; height:88px; border-radius:12px; object-fit:cover; flex-shrink:0; }

        /* Avatar placeholder: แยก guest (เทา) / staff (น้ำเงิน) */
        .ci-avatar-placeholder {
          width:88px; height:88px; border-radius:12px;
          display:flex; align-items:center; justify-content:center;
          font-size:28px; font-weight:700; flex-shrink:0;
        }
        .ci-avatar-placeholder.guest { background:linear-gradient(135deg,#e5e7eb,#d1d5db); color:#4b5563; }
        .ci-avatar-placeholder.staff { background:linear-gradient(135deg,#dbeafe,#bfdbfe); color:#1d4ed8; }

        .ci-info  { flex:1; min-width:0; }
        .ci-name  { font-size:15px; font-weight:700; color:#1a1a1a; margin-bottom:10px; }
        .ci-meta  { display:flex; flex-direction:column; gap:5px; }
        .ci-meta-item { display:flex; align-items:center; gap:6px; font-size:12.5px; color:#6b7280; }
        .ci-meta-item svg { flex-shrink:0; opacity:.7; }
        .ci-right { display:flex; align-items:center; gap:24px; flex-shrink:0; }
        /* เลขห้อง — บรรทัดเดียว "ห้องเลขที่ X" */
        .ci-room-block  { display:flex; align-items:baseline; gap:5px; white-space:nowrap; }
        .ci-room-label  { font-size:12px; color:#9ca3af; }
        .ci-room-num    { font-size:15px; font-weight:700; color:#9ca3af; }
        .ci-btn-checkin { display:inline-flex; align-items:center; gap:6px; padding:10px 26px; background:#1a1a1a; color:#fff; border:none; border-radius:999px; font-size:14px; font-weight:600; cursor:pointer; transition:background .15s; font-family:'Noto Sans Thai',sans-serif; white-space:nowrap; }
        .ci-btn-checkin:hover { background:#333; }

        .ci-empty { text-align:center; padding:60px 20px; color:#9ca3af; background:#fff; border-radius:14px; border:1px solid #e0e0e0; }
        .ci-empty-icon { font-size:40px; margin-bottom:10px; }
        .ci-empty-text { font-size:14px; font-weight:500; }
        .ci-loading { text-align:center; padding:60px 20px; color:#9ca3af; font-size:14px; background:#fff; border-radius:14px; border:1px solid #e0e0e0; }
        @keyframes ci-spin { to { transform:rotate(360deg); } }
        .ci-spinner { width:28px; height:28px; border:3px solid #e5e7eb; border-top-color:#1a1a1a; border-radius:50%; animation:ci-spin .7s linear infinite; margin:0 auto 12px; }

        /* Form page */
        .ci-form-page { background:#fff; border-radius:16px; border:1px solid #e0e0e0; padding:32px 40px; }
        .ci-form-top  { display:flex; align-items:center; gap:14px; margin-bottom:28px; }
        .ci-back-btn  { display:inline-flex; align-items:center; gap:6px; padding:7px 16px; border-radius:8px; border:1px solid #e0e0e0; background:#fff; color:#374151; font-size:13px; font-weight:500; cursor:pointer; font-family:'Noto Sans Thai',sans-serif; }
        .ci-back-btn:hover { background:#f3f4f6; }
        .ci-form-title { font-size:20px; font-weight:700; color:#1a1a1a; display:flex; align-items:center; gap:8px; }

        .ci-form-body { display:grid; grid-template-columns:1fr 360px; gap:36px; }

        .ci-form-left { display:flex; flex-direction:column; }

        .ci-guest-row    { display:flex; align-items:center; gap:14px; margin-bottom:22px; }
        .ci-guest-avatar { width:68px; height:68px; border-radius:50%; object-fit:cover; flex-shrink:0; }

        /* Form avatar placeholder: แยก guest / staff */
        .ci-guest-avatar-ph {
          width:68px; height:68px; border-radius:50%;
          display:flex; align-items:center; justify-content:center;
          font-size:24px; font-weight:700; flex-shrink:0;
        }
        .ci-guest-avatar-ph.guest { background:linear-gradient(135deg,#e5e7eb,#d1d5db); color:#4b5563; }
        .ci-guest-avatar-ph.staff { background:linear-gradient(135deg,#dbeafe,#bfdbfe); color:#1d4ed8; }

        .ci-guest-gname { font-size:16px; font-weight:700; color:#1a1a1a; }
        .ci-guest-email { font-size:12.5px; color:#6b7280; margin-top:2px; }
        .ci-guest-age   { font-size:12.5px; color:#6b7280; }

        .ci-readcard-btn { display:inline-flex; align-items:center; justify-content:center; gap:8px; width:100%; padding:12px 16px; background:#1d4ed8; color:#fff; border:none; border-radius:10px; font-size:14px; font-weight:600; cursor:pointer; font-family:'Noto Sans Thai',sans-serif; transition:background .15s, opacity .15s; }
        .ci-readcard-btn:hover:not(:disabled) { background:#1e40af; }
        .ci-readcard-btn:disabled { opacity:.7; cursor:not-allowed; }
        .ci-readcard-spinner { width:15px; height:15px; border:2px solid rgba(255,255,255,.5); border-top-color:#fff; border-radius:50%; animation:ci-spin .7s linear infinite; }
        .ci-agent-status { display:inline-flex; align-items:center; gap:7px; margin-top:10px; padding:5px 12px; border-radius:999px; font-size:12px; font-weight:600; cursor:pointer; border:1px solid transparent; font-family:'Noto Sans Thai',sans-serif; transition:background .15s; }
        .ci-agent-status .ci-agent-dot { width:8px; height:8px; border-radius:50%; flex-shrink:0; }
        .ci-agent-status.online   { background:#f0fdf4; border-color:#bbf7d0; color:#166534; }
        .ci-agent-status.online   .ci-agent-dot { background:#16a34a; box-shadow:0 0 0 3px rgba(22,163,74,.15); }
        .ci-agent-status.card     { background:#dcfce7; border-color:#86efac; color:#15803d; }
        .ci-agent-status.card     .ci-agent-dot { background:#15803d; box-shadow:0 0 0 3px rgba(21,128,61,.18); }
        .ci-agent-status.no-reader,
        .ci-agent-status.no-pcsc  { background:#fffbeb; border-color:#fde68a; color:#92400e; }
        .ci-agent-status.no-reader .ci-agent-dot,
        .ci-agent-status.no-pcsc  .ci-agent-dot { background:#f59e0b; }
        .ci-agent-status.offline,
        .ci-agent-status.error    { background:#fef2f2; border-color:#fecaca; color:#991b1b; }
        .ci-agent-status.offline  .ci-agent-dot,
        .ci-agent-status.error    .ci-agent-dot { background:#dc2626; }
        .ci-agent-status.checking { background:#f3f4f6; border-color:#e5e7eb; color:#6b7280; }
        .ci-agent-status.checking .ci-agent-dot { background:#9ca3af; animation:ci-pulse 1s ease-in-out infinite; }
        @keyframes ci-pulse { 0%,100%{opacity:1} 50%{opacity:.3} }

        .ci-readcard-hint { font-size:11.5px; color:#9ca3af; margin:7px 0 18px; display:flex; align-items:center; gap:5px; }

        .ci-id-label  { font-size:13px; font-weight:600; color:#374151; margin-bottom:8px; margin-top:0; }
        .ci-id-row    { display:flex; align-items:center; gap:8px; }
        .ci-id-input  { flex:1; padding:10px 14px; border-radius:8px; border:1px solid #e0e0e0; font-size:13.5px; font-family:'Noto Sans Thai',sans-serif; background:#fff; color:#1a1a1a; outline:none; }
        .ci-id-input:focus { border-color:#9ca3af; }
        .ci-id-input::placeholder { color:#bbb; }
        .ci-cam-btn   { position:relative; width:42px; height:42px; border-radius:8px; background:#f3f4f6; border:1px solid #e0e0e0; display:flex; align-items:center; justify-content:center; cursor:pointer; color:#6b7280; flex-shrink:0; transition:background .15s,border-color .15s; }
        .ci-cam-btn:hover { background:#e5e7eb; border-color:#9ca3af; color:#374151; }
        .ci-cam-btn.busy  { opacity:.5; pointer-events:none; }
        .ci-file-name { display:flex; align-items:center; gap:5px; font-size:11.5px; color:#16a34a; margin-top:5px; font-weight:500; }

        .ci-fields { display:grid; grid-template-columns:1fr 1fr; gap:16px 24px; margin-top:22px; }
        .ci-field  { display:flex; flex-direction:column; gap:5px; }
        .ci-field label { font-size:12.5px; font-weight:600; color:#374151; }
        .ci-field input, .ci-field select {
          padding:10px 14px; border-radius:8px; border:1px solid #e0e0e0;
          font-size:13.5px; font-family:'Noto Sans Thai',sans-serif;
          background:#fff; color:#1a1a1a; outline:none;
        }
        .ci-field input:focus, .ci-field select:focus { border-color:#9ca3af; }
        .ci-field input::placeholder { color:#bbb; }
        /* ลูกศร dropdown เฉพาะ select เท่านั้น — input ธรรมดาไม่ต้องมี */
        .ci-field select {
          cursor:pointer;
          appearance:none; -webkit-appearance:none;
          padding-right:34px;
          background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%239ca3af' d='M6 8L1 3h10z'/%3E%3C/svg%3E");
          background-repeat:no-repeat; background-position:right 12px center;
        }

        /* Right panel */
        .ci-form-right   { background:#f9f9f9; border-radius:16px; border:1px solid #e8e8e8; padding:24px; display:flex; flex-direction:column; gap:14px; }
        .ci-sum-title    { font-size:15px; font-weight:700; color:#1a1a1a; }
        .ci-room-img     { width:100%; height:160px; object-fit:cover; border-radius:12px; }
        .ci-room-img-ph  { width:100%; height:160px; border-radius:12px; background:#e5e7eb; display:flex; align-items:center; justify-content:center; color:#9ca3af; font-size:13px; }
        .ci-sum-roomname { font-size:17px; font-weight:700; color:#1a1a1a; margin-top:4px; }
        .ci-sum-meta     { display:flex; flex-direction:column; gap:7px; }
        .ci-sum-item     { display:flex; align-items:center; gap:7px; font-size:12.5px; color:#6b7280; }
        .ci-sum-divider  { border:none; border-top:1px solid #e0e0e0; margin:2px 0; }

        /* Price */
        .ci-price-block   { display:flex; flex-direction:column; gap:9px; }
        .ci-price-row     { display:flex; justify-content:space-between; align-items:center; }
        .ci-price-label   { font-size:13px; color:#6b7280; }
        .ci-price-val     { font-size:13.5px; font-weight:600; color:#1a1a1a; }
        .ci-price-val.paid { color:#16a34a; }
        .ci-price-divider { border-top:1px dashed #d1d5db; margin:2px 0; }
        .ci-balance-label { font-size:14px; font-weight:700; color:#1a1a1a; }
        .ci-balance-val   { font-size:20px; font-weight:800; }
        .ci-balance-val.due       { color:#dc2626; }
        .ci-balance-val.paid-full { font-size:14px; color:#16a34a; font-weight:700; }

        /* Payment */
        .ci-pay-btn { display:inline-flex; align-items:center; justify-content:center; gap:7px; width:100%; padding:11px; background:#16a34a; color:#fff; border:none; border-radius:10px; font-size:14px; font-weight:600; cursor:pointer; font-family:'Noto Sans Thai',sans-serif; transition:background .15s; }
        .ci-pay-btn:hover { background:#15803d; }
        .ci-paid-note { display:flex; align-items:center; justify-content:space-between; gap:8px; background:#f0fdf4; border:1px solid #bbf7d0; border-radius:9px; padding:8px 12px; font-size:12px; color:#166534; font-weight:500; }
        .ci-undo-btn { background:#fff; border:1px solid #d1d5db; border-radius:7px; padding:4px 12px; font-size:12px; color:#374151; font-weight:500; cursor:pointer; font-family:'Noto Sans Thai',sans-serif; flex-shrink:0; }
        .ci-undo-btn:hover { background:#f3f4f6; }

        /* Payment popup */
        .ci-pay-overlay { position:fixed; inset:0; background:rgba(0,0,0,.45); z-index:2000; display:flex; align-items:center; justify-content:center; animation:ci-fadein .15s ease; }
        .ci-pay-modal { background:#fff; border-radius:18px; width:92vw; max-width:400px; padding:26px 28px; box-shadow:0 12px 40px rgba(0,0,0,.2); }
        .ci-pay-title { font-size:17px; font-weight:700; color:#1a1a1a; margin-bottom:4px; display:flex; align-items:center; gap:8px; }
        .ci-pay-sub   { font-size:12.5px; color:#6b7280; margin-bottom:18px; }
        .ci-pay-amount-box { background:#f9fafb; border:1px solid #e5e7eb; border-radius:12px; padding:16px; text-align:center; margin-bottom:18px; }
        .ci-pay-amount-label { font-size:12px; color:#6b7280; margin-bottom:4px; display:flex; align-items:center; justify-content:center; gap:5px; }
        .ci-pay-amount-val { font-size:28px; font-weight:800; color:#1a1a1a; }
        .ci-pay-methods { display:grid; grid-template-columns:1fr 1fr; gap:10px; margin-bottom:18px; }
        .ci-pay-method { display:flex; flex-direction:column; align-items:center; gap:8px; padding:16px 10px; border-radius:12px; border:2px solid #e5e7eb; background:#fff; cursor:pointer; font-size:13.5px; font-weight:600; color:#374151; font-family:'Noto Sans Thai',sans-serif; transition:border-color .15s, background .15s; }
        .ci-pay-method.active { border-color:#16a34a; background:#f0fdf4; color:#166534; }
        .ci-pay-qrbox { background:#f9fafb; border:1px dashed #d1d5db; border-radius:12px; padding:18px; display:flex; flex-direction:column; align-items:center; gap:8px; margin-bottom:18px; color:#6b7280; font-size:12.5px; }
        .ci-pay-actions { display:flex; gap:10px; }
        .ci-pay-cancel { flex:1; padding:12px; border-radius:999px; border:1px solid #d1d5db; background:#fff; color:#374151; font-size:14px; font-weight:600; cursor:pointer; font-family:'Noto Sans Thai',sans-serif; }
        .ci-pay-cancel:hover { background:#f3f4f6; }
        .ci-pay-ok { flex:1.4; padding:12px; border-radius:999px; border:none; background:#16a34a; color:#fff; font-size:14px; font-weight:700; cursor:pointer; font-family:'Noto Sans Thai',sans-serif; transition:background .15s; }
        .ci-pay-ok:hover { background:#15803d; }

        .ci-confirm-btn { width:100%; padding:14px; background:#1a1a1a; color:#fff; border:none; border-radius:999px; font-size:15px; font-weight:700; cursor:pointer; font-family:'Noto Sans Thai',sans-serif; margin-top:4px; transition:background .15s; }
        .ci-confirm-btn:hover    { background:#333; }
        .ci-confirm-btn:disabled { background:#9ca3af; cursor:not-allowed; }

        @keyframes ci-fadein { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
        .ci-toast { position:fixed; top:76px; right:28px; padding:12px 20px; border-radius:10px; font-size:13.5px; font-weight:500; box-shadow:0 8px 24px rgba(0,0,0,.15); z-index:3000; animation:ci-fadein .2s ease; display:flex; align-items:center; gap:8px; }
        .ci-toast.success { background:#f0fdf4; color:#166534; border:1px solid #bbf7d0; }
        .ci-toast.error   { background:#fef2f2; color:#991b1b; border:1px solid #fecaca; }

        /* ── ส่วนที่แอดมินกรอกเพิ่ม (บริษัท / รถ / ประเภทผู้เข้าพัก) ── */
        .ci-admin-sec       { margin-top:26px; border-top:1px solid #eceff2; padding-top:20px; }
        .ci-admin-sec-head  { display:flex; align-items:center; gap:8px; margin-bottom:4px; }
        .ci-admin-sec-title { font-size:14px; font-weight:700; color:#1a1a1a; }
        .ci-admin-sec-sub   { font-size:11.5px; color:#9ca3af; margin-bottom:14px; }
        .ci-admin-group     { font-size:12px; font-weight:700; color:#6b7280; margin:18px 0 8px; letter-spacing:.3px; }
        .ci-auto-hint       { font-size:11px; color:#1d4ed8; margin-top:4px; display:flex; align-items:center; gap:4px; }
        .ci-field-note      { font-size:11px; color:#9ca3af; margin-top:-6px; margin-bottom:10px; }

        /* ── แบนเนอร์ลูกค้าเก่า ── */
        .ci-known-banner { display:flex; align-items:center; gap:10px; background:#f0fdf4; border:1px solid #bbf7d0; border-radius:10px; padding:10px 14px; margin:14px 0 0; font-size:12.5px; color:#166534; }
        .ci-known-banner strong { font-weight:700; }

        /* ── ตารางประวัติการเข้าพักครั้งก่อนๆ ── */
        .ci-hist        { margin-top:26px; border-top:1px solid #eceff2; padding-top:20px; }
        .ci-hist-title  { font-size:14px; font-weight:700; color:#1a1a1a; margin-bottom:3px; display:flex; align-items:center; gap:8px; }
        .ci-hist-sub    { font-size:11.5px; color:#9ca3af; margin-bottom:12px; }
        .ci-hist-wrap   { overflow-x:auto; border:1px solid #eceff2; border-radius:10px; }
        .ci-hist-table  { width:100%; border-collapse:collapse; font-size:12px; min-width:620px; }
        .ci-hist-table th { background:#f8fafc; text-align:left; padding:9px 12px; font-weight:600; color:#64748b; border-bottom:1px solid #e2e8f0; white-space:nowrap; }
        .ci-hist-table td { padding:9px 12px; border-bottom:1px solid #f3f4f6; color:#374151; white-space:nowrap; }
        .ci-hist-table tr:last-child td { border-bottom:none; }
        .ci-hist-table tr:nth-child(even) td { background:#fafafa; }
        .ci-hist-due    { color:#dc2626; font-weight:600; }
        .ci-hist-ok     { color:#16a34a; font-weight:600; }
        .ci-type-chip   { display:inline-block; padding:2px 9px; border-radius:999px; font-size:10.5px; font-weight:600; color:#fff; }

        /* ── การจองจากปฏิทินที่ยังไม่ถูกบันทึก ── */
        .ci-draft-banner { display:flex; align-items:center; gap:9px; background:#fffbeb; border:1px solid #fde68a; color:#92400e; border-radius:12px; padding:11px 15px; font-size:12.5px; font-weight:600; margin-bottom:18px; }
        .ci-draft-banner svg { flex-shrink:0; }

        /* ── จองหลายห้องพร้อมกัน: ป้ายในรายการ + แถบลำดับห้อง + ปุ่มถัดไป/ย้อนกลับ ── */
        .ci-group-badge { display:inline-flex; align-items:center; gap:4px; margin-left:8px; padding:2px 9px; border-radius:999px; background:#eef2ff; border:1px solid #c7d2fe; color:#3730a3; font-size:11px; font-weight:600; vertical-align:middle; }

        .ci-steps       { background:#f8fafc; border:1px solid #e8ecf1; border-radius:12px; padding:14px 16px; margin-bottom:22px; }
        .ci-steps-head  { display:flex; align-items:baseline; gap:10px; flex-wrap:wrap; margin-bottom:10px; }
        .ci-steps-title { font-size:13.5px; font-weight:700; color:#1a1a1a; }
        .ci-steps-hint  { font-size:11.5px; color:#9ca3af; }
        .ci-steps-row   { display:flex; gap:8px; flex-wrap:wrap; }
        .ci-step        { display:inline-flex; align-items:center; gap:6px; padding:7px 14px; border-radius:999px; border:1px solid #e0e0e0; background:#fff; color:#6b7280; font-size:12.5px; font-weight:600; cursor:pointer; font-family:'Noto Sans Thai',sans-serif; transition:border-color .15s, color .15s, background .15s; }
        .ci-step:hover  { border-color:#9ca3af; color:#374151; }
        .ci-step-no     { display:inline-flex; align-items:center; justify-content:center; width:18px; height:18px; border-radius:50%; background:#eef0f2; color:#6b7280; font-size:11px; font-weight:700; }
        .ci-step.done       { border-color:#bbf7d0; background:#f0fdf4; color:#166534; }
        .ci-step.done .ci-step-no { background:#dcfce7; color:#166534; }
        .ci-step-check  { font-size:11px; }
        .ci-step.active { border-color:#1a1a1a; background:#1a1a1a; color:#fff; }
        .ci-step.active .ci-step-no { background:rgba(255,255,255,.22); color:#fff; }

        .ci-copy-prev   { display:inline-flex; align-items:center; gap:6px; align-self:flex-start; margin:0 0 16px; padding:6px 13px; border-radius:8px; border:1px dashed #c7d2fe; background:#f8faff; color:#3730a3; font-size:12px; font-weight:600; cursor:pointer; font-family:'Noto Sans Thai',sans-serif; }
        .ci-copy-prev:hover { background:#eef2ff; }

        .ci-nav-row     { display:flex; gap:10px; margin-top:4px; }
        .ci-nav-btn     { flex:1; display:inline-flex; align-items:center; justify-content:center; gap:6px; padding:13px 10px; border-radius:999px; border:1px solid #d1d5db; background:#fff; color:#374151; font-size:14px; font-weight:600; cursor:pointer; font-family:'Noto Sans Thai',sans-serif; transition:background .15s; }
        .ci-nav-btn:hover:not(:disabled) { background:#f3f4f6; }
        .ci-nav-btn:disabled { opacity:.45; cursor:not-allowed; }
        .ci-nav-btn.next { flex:1.3; border-color:#1d4ed8; background:#1d4ed8; color:#fff; }
        .ci-nav-btn.next:hover:not(:disabled) { background:#1e40af; }
        .ci-confirm-btn.group { flex:1.6; width:auto; margin-top:0; padding:13px 10px; font-size:14px; }

        @media(max-width:820px){
          .ci-form-body { grid-template-columns:1fr; }
          .ci-fields    { grid-template-columns:1fr; }
        }
      `}</style>

      <div className="ci-page">
        <div className="ci-wrap">

          {selected ? (
            <div className="ci-form-page">

              <div className="ci-form-top">
                <button className="ci-back-btn" onClick={handleBack}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M19 12H5"/><path d="M12 19l-7-7 7-7"/>
                  </svg>
                  {t(lang, "ciBack")}
                </button>
                <div className="ci-form-title"><span>🛎️</span> {t(lang, "ciTitle")}</div>
              </div>

              {/* จองจากปฏิทินแบบ "เช็คอินเลย" — ยังไม่มีอะไรอยู่ในระบบจนกว่าจะกดยืนยันสำเร็จ */}
              {draft && (
                <div className="ci-draft-banner">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"/><path d="M12 8v5"/><path d="M12 16.5v.01"/>
                  </svg>
                  <span>{t(lang, "ciDraftBanner")}</span>
                </div>
              )}

              {/* แถบลำดับห้อง — โผล่เฉพาะตอนจองมาหลายห้องพร้อมกัน กดข้ามไปห้องไหนก็ได้ */}
              {group.length > 1 && (
                <div className="ci-steps">
                  <div className="ci-steps-head">
                    <span className="ci-steps-title">{t(lang, "ciGroupStep", gIdx + 1, group.length)}</span>
                    <span className="ci-steps-hint">{t(lang, "ciGroupHint")}</span>
                  </div>
                  <div className="ci-steps-row">
                    {group.map((b, i) => {
                      const f    = i === gIdx ? form : gForms[i];
                      const done = !!(String(f?.fullName || "").trim() || String(f?.idCard || "").trim());
                      return (
                        <button
                          key={b._id}
                          type="button"
                          className={`ci-step${i === gIdx ? " active" : ""}${done ? " done" : ""}`}
                          onClick={() => goToRoom(i)}
                        >
                          <span className="ci-step-no">{i + 1}</span>
                          {t(lang, "ciRoomLabel")} {b.roomUnit?.roomNumber ?? "—"}
                          {done && i !== gIdx && <span className="ci-step-check">✓</span>}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="ci-form-body">

                {/* LEFT */}
                <div className="ci-form-left">
                  <div className="ci-guest-row">
                    <div>
                      <div className="ci-guest-gname">
                        {group.length > 1
                          ? `${t(lang, "ciRoomLabel")} ${selected.roomUnit?.roomNumber ?? "—"}`
                          : getGuestName(selected)}
                      </div>
                      {group.length > 1
                        ? <div className="ci-guest-email">{t(lang, "ciGroupBookedBy")} {getGuestName(selected)}</div>
                        : selected.user?.email && <div className="ci-guest-email">{selected.user.email}</div>}
                      {group.length === 1 && selected.user?.age &&
                        <div className="ci-guest-age">{t(lang, "ciAge")} {selected.user.age}</div>}
                    </div>
                  </div>

                  {gIdx > 0 && (
                    <button type="button" className="ci-copy-prev" onClick={copyFromPrevRoom}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="9" y="9" width="13" height="13" rx="2"/>
                        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                      </svg>
                      {t(lang, "ciCopyPrev")}
                    </button>
                  )}

                  <button
                    type="button"
                    className="ci-readcard-btn"
                    onClick={readIdCard}
                    disabled={readingCard || agentStatus === "no-reader" || agentStatus === "no-pcsc"}
                  >
                    {readingCard ? (
                      <><span className="ci-readcard-spinner" />{t(lang, "ciReadingCard")}</>
                    ) : (
                      <>
                        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="2" y="5" width="20" height="14" rx="2"/>
                          <circle cx="8" cy="11" r="2"/>
                          <path d="M6 16c0-1.5 1-2.5 2-2.5s2 1 2 2.5"/>
                          <line x1="14" y1="10" x2="18" y2="10"/>
                          <line x1="14" y1="14" x2="18" y2="14"/>
                        </svg>
                        {t(lang, "ciReadCard")}
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    className={`ci-agent-status ${agentStatus}`}
                    onClick={checkAgent}
                    title={t(lang, "ciReadCardHint")}
                  >
                    <span className="ci-agent-dot" />
                    {t(lang, AGENT_STATUS_LABEL[agentStatus] || "ciAgentChecking")}
                  </button>
                  <div className="ci-readcard-hint">{t(lang, "ciReadCardHint")}</div>

                  <div className="ci-id-label">{t(lang, "ciIdCard")}</div>
                  <div className="ci-id-row">
                    <input
                      className="ci-id-input"
                      placeholder={t(lang, "ciIdCardPh")}
                      value={form.idCard}
                      onChange={e => setForm(f => ({ ...f, idCard: e.target.value }))}
                      onBlur={e => lookupGuest({ idCard: e.target.value })}
                    />
                    <label className={`ci-cam-btn${ocrBusy ? " busy" : ""}`} title={t(lang, "ciOcrPick")}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
                        <circle cx="12" cy="13" r="4"/>
                      </svg>
                      <input type="file" accept="image/*" style={{ display:"none" }}
                        onChange={e => {
                          const file = e.target.files[0];
                          e.target.value = "";           // เคลียร์ input ทันที — เลือกไฟล์เดิมซ้ำได้ และไม่ค้าง handle ไว้
                          if (file) runOcrOnFile(file);  // อ่านข้อมูลอย่างเดียว ไม่เก็บรูป
                        }} />
                    </label>
                  </div>
                  {lookingUp && (
                    <div className="ci-file-name" style={{ color: "#6b7280" }}>
                      <span className="ci-readcard-spinner" style={{ borderTopColor: "#6b7280", borderColor: "#e5e7eb", width: 11, height: 11 }} />
                      {t(lang, "ciLookingUp")}
                    </div>
                  )}
                  {ocrBusy && (
                    <div className="ci-file-name" style={{ color: "#6b7280" }}>
                      <span className="ci-readcard-spinner" style={{ borderTopColor: "#6b7280", borderColor: "#e5e7eb", width: 11, height: 11 }} />
                      {t(lang, "ciOcrReading")} {ocrPct}%
                    </div>
                  )}
                  {!ocrBusy && ocrDone && (
                    <div className="ci-file-name">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"/>
                      </svg>
                      {t(lang, "ciOcrDone")}
                    </div>
                  )}

                  <div className="ci-fields">
                    {[
                      [t(lang, "ciFieldName"),        "fullName",    ""],
                      [t(lang, "ciFieldAddress"),     "address",     ""],
                      [t(lang, "ciFieldBirthDate"),   "birthDate",   ""],
                      [t(lang, "ciFieldGender"),      "gender",      ""],
                      [t(lang, "ciFieldCountry"),     "country",     ""],
                      [t(lang, "ciFieldNationality"), "nationality", ""],
                      [t(lang, "ciFieldPhone"),       "phone",       ""],
                      [t(lang, "ciFieldExtra"),       "extra",       ""],
                    ].map(([label, key, placeholder]) => (
                      <div className="ci-field" key={key}>
                        <label>{label}</label>

                        {key === "birthDate" ? (
                          <input
                            type="date"
                            value={form.birthDate}
                            onChange={e =>
                              setForm(f => ({ ...f, birthDate: e.target.value }))
                            }
                          />
                        ) : key === "gender" ? (
                          <select
                            value={form.gender}
                            onChange={e => setForm(f => ({ ...f, gender: e.target.value }))}
                          >
                            <option value="">{t(lang, "ciGenderSelect")}</option>
                            <option value="ชาย">{t(lang, "ciGenderM")}</option>
                            <option value="หญิง">{t(lang, "ciGenderF")}</option>
                            <option value="เพศทางเลือก">{t(lang, "ciGenderOther")}</option>
                            <option value="ไม่ระบุ">{t(lang, "ciGenderNA")}</option>
                          </select>
                        ) : (
                          <input
                            placeholder={placeholder}
                            value={form[key]}
                            onChange={e =>
                              setForm(f => ({ ...f, [key]: e.target.value }))
                            }
                          />
                        )}
                      </div>
                    ))}
                  </div>

                  {/* ══ ช่องที่แอดมินกรอกเพิ่ม ══ */}
                  <div className="ci-admin-sec">
                    <div className="ci-admin-sec-head">
                      <span className="ci-admin-sec-title">{t(lang, "ciAdminSection")}</span>
                    </div>
                    <div className="ci-admin-sec-sub">{t(lang, "ciAdminSectionSub")}</div>

                    {/* ประเภทผู้เข้าพัก — dimension หลักของรายงานวิเคราะห์ วางบนสุดกันถูกข้าม */}
                    <div className="ci-fields" style={{ marginTop: 0 }}>
                      <div className="ci-field">
                        <label>{t(lang, "ciGuestType")}</label>
                        <select
                          value={effectiveGuestType}
                          onChange={e => setForm(f => ({ ...f, guestType: e.target.value }))}
                        >
                          {GUEST_TYPES.filter(g => g.value !== "unknown").map(g => (
                            <option key={g.value} value={g.value}>
                              {lang === "en" ? g.en : g.th}
                            </option>
                          ))}
                        </select>
                        {!form.guestType && (
                          <div className="ci-auto-hint">
                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M12 2l2.4 7.4H22l-6 4.4 2.3 7.2-6.3-4.6-6.3 4.6L7.9 13.8 2 9.4h7.6z"/>
                            </svg>
                            {t(lang, "ciTypeAuto")}
                          </div>
                        )}
                      </div>
                      <div className="ci-field">
                        <label>{t(lang, "ciStayPurpose")}</label>
                        <select
                          value={form.stayPurpose}
                          onChange={e => setForm(f => ({ ...f, stayPurpose: e.target.value }))}
                        >
                          <option value="">{t(lang, "ciPurposeSelect")}</option>
                          {STAY_PURPOSES.map(p => (
                            <option key={p.value} value={p.value}>
                              {lang === "en" ? p.en : p.th}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* ข้อมูลบริษัท */}
                    <div className="ci-admin-group">{t(lang, "ciCompanyGroup")}</div>
                    <div className="ci-fields" style={{ marginTop: 0 }}>
                      {[
                        [t(lang, "ciCompanyName"),     "companyName"],
                        [t(lang, "ciCompanyTaxId"),    "companyTaxId"],
                        [t(lang, "ciCompanyAddress"),  "companyAddress"],
                        [t(lang, "ciCompanyBranchNo"), "companyBranchNo"],
                      ].map(([label, key]) => (
                        <div className="ci-field" key={key}>
                          <label>{label}</label>
                          <input
                            value={form[key]}
                            onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                          />
                        </div>
                      ))}
                    </div>

                    {/* ข้อมูลรถ — ไม่เข้าใบเสร็จ */}
                    <div className="ci-admin-group">{t(lang, "ciCarGroup")}</div>
                    <div className="ci-field-note">{t(lang, "ciCarNote")}</div>
                    <div className="ci-fields" style={{ marginTop: 0 }}>
                      <div className="ci-field">
                        <label>{t(lang, "ciCarPlate")}</label>
                        <input
                          value={form.carPlate}
                          onChange={e => {
                            const carPlate = e.target.value;
                            // ป้ายทะเบียนไทยมีชื่อจังหวัดอยู่ในตัว — เติมให้อัตโนมัติถ้ายังไม่ได้เลือก
                            const found = parseCarProvince(carPlate);
                            setForm(f => ({
                              ...f, carPlate,
                              carProvince: f.carProvince || found,
                            }));
                          }}
                        />
                      </div>
                      <div className="ci-field">
                        <label>{t(lang, "ciCarProvince")}</label>
                        <SearchableSelect
                          value={form.carProvince}
                          onChange={v => setForm(f => ({ ...f, carProvince: v }))}
                          options={TH_PROVINCES}
                          placeholder={t(lang, "ciProvinceSearchPh")}
                        />
                      </div>
                      <div className="ci-field">
                        <label>{t(lang, "ciCarBrand")}</label>
                        <input
                          value={form.carBrand}
                          onChange={e => setForm(f => ({ ...f, carBrand: e.target.value }))}
                        />
                      </div>
                      <div className="ci-field">
                        <label>{t(lang, "ciCarColor")}</label>
                        <input
                          value={form.carColor}
                          onChange={e => setForm(f => ({ ...f, carColor: e.target.value }))}
                        />
                      </div>
                    </div>
                  </div>

                  {/* ══ ประวัติการเข้าพักครั้งก่อนๆ ══ */}
                  {guestMatch?.found && (
                    <>
                      <div className="ci-known-banner">
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M20 6L9 17l-5-5"/>
                        </svg>
                        <span>
                          <strong>{t(lang, "ciReturningGuest")}</strong>
                          {" · "}{t(lang, "ciStayCount", guestMatch.stayCount)}
                          {guestMatch.outstanding > 0 && (
                            <span className="ci-hist-due">
                              {" · "}{t(lang, "ciHistOutstanding")} {fmtMoney(guestMatch.outstanding)}
                            </span>
                          )}
                        </span>
                      </div>

                      {guestMatch.stays?.length > 0 && (
                        <div className="ci-hist">
                          <div className="ci-hist-title">
                            <span>🗂</span> {t(lang, "ciHistTitle")}
                          </div>
                          <div className="ci-hist-sub">{t(lang, "ciHistSub")}</div>
                          <div className="ci-hist-wrap">
                            <table className="ci-hist-table">
                              <thead>
                                <tr>
                                  <th>{t(lang, "ciHistDate")}</th>
                                  <th>{t(lang, "ciHistRoom")}</th>
                                  <th>{t(lang, "ciHistType")}</th>
                                  <th style={{ textAlign: "right" }}>{t(lang, "ciHistTotal")}</th>
                                  <th style={{ textAlign: "right" }}>{t(lang, "ciHistPaid")}</th>
                                  <th style={{ textAlign: "right" }}>{t(lang, "ciHistDue")}</th>
                                  <th>{t(lang, "ciHistMethod")}</th>
                                </tr>
                              </thead>
                              <tbody>
                                {guestMatch.stays.map(s => (
                                  <tr key={s._id}>
                                    <td>{fmtDate(s.checkIn)} – {fmtDate(s.checkOut)}</td>
                                    <td>{s.roomNumber || "—"}</td>
                                    <td>
                                      <span className="ci-type-chip" style={{ background: guestTypeColor(s.guestType) }}>
                                        {guestTypeLabel(s.guestType, lang)}
                                      </span>
                                    </td>
                                    <td style={{ textAlign: "right" }}>{fmtMoney(s.totalAmount)}</td>
                                    <td style={{ textAlign: "right" }}>{fmtMoney(s.paidAmount)}</td>
                                    <td style={{ textAlign: "right" }}
                                        className={s.outstanding > 0 ? "ci-hist-due" : "ci-hist-ok"}>
                                      {s.outstanding > 0 ? fmtMoney(s.outstanding) : "—"}
                                    </td>
                                    <td>
                                      {s.paymentMethod === "cash" ? t(lang, "ciCash")
                                        : s.paymentMethod === "qr" ? "QR Code"
                                        : "—"}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>

                {/* RIGHT */}
                <div className="ci-form-right">
                  <div className="ci-sum-title">{t(lang, "ciBookingDetail")}</div>

                  {/* Avatar + ชื่อ */}
                  <div className="ci-guest-row" style={{ marginBottom: 0 }}>
                    <AvatarWithFallback
                      src={avatarSrc}
                      role={getAvatarRole(selected?.user)}
                      imgClass="ci-guest-avatar"
                      phClass="ci-guest-avatar-ph"
                    />
                    <div>
                      <div className="ci-guest-gname">{getGuestName(selected)}</div>
                      {selected.user?.email && <div className="ci-guest-email">{selected.user.email}</div>}
                    </div>
                  </div>

                  <hr className="ci-sum-divider" />

                  {(() => {
                    const img =
                      selected.roomType?.images?.[0] ??
                      selected.roomType?.image ??
                      selected.roomUnit?.image ??
                      null;
                    return img
                      ? <img className="ci-room-img" src={`/uploads/${img}`} alt="" />
                      : <div className="ci-room-img-ph">{t(lang, "ciNoImage")}</div>;
                  })()}

                  <div className="ci-sum-roomname">
                    {selected.roomType?.name ?? t(lang, "ciDefaultRoom")} : {selected.roomUnit?.roomNumber ?? "—"}
                  </div>

                  <div className="ci-sum-meta">
                    <div className="ci-sum-item">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
                        <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                      </svg>
                      {t(lang, "ciRoomFor")} : {selected.roomType?.adults ?? 1} {t(lang, "ciPersons")}
                    </div>
                    <div className="ci-sum-item">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/>
                        <line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
                      </svg>
                      {t(lang, "ciBookedOn")} : {selected.isDraft ? t(lang, "ciDraftNotSaved") : fmtDateTime(selected.createdAt)}
                    </div>
                    <div className="ci-sum-item">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M5 12h14"/><path d="M12 5l7 7-7 7"/>
                      </svg>
                      {t(lang, "ciCheckIn")} : {fmtDate(selected.checkIn)}
                    </div>
                    <div className="ci-sum-item">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M19 12H5"/><path d="M12 19l-7-7 7-7"/>
                      </svg>
                      {t(lang, "ciCheckOut")} : {fmtDate(selected.checkOut)}
                    </div>
                  </div>

                  <hr className="ci-sum-divider" />

                  {/* Price breakdown */}
                  <div className="ci-price-block">
                    {group.length > 1 && (
                      <div className="ci-price-row">
                        <span className="ci-price-label">{t(lang, "ciGroupTotal", group.length)}</span>
                        <span className="ci-price-val">{fmtMoney(groupTotal)} THB</span>
                      </div>
                    )}
                    <div className="ci-price-row">
                      <span className="ci-price-label">
                        {group.length > 1 ? t(lang, "ciRoomPrice") : t(lang, "ciTotalPrice")}
                      </span>
                      <span className="ci-price-val">{total ? `${fmtMoney(total)} THB` : "—"}</span>
                    </div>
                    <div className="ci-price-row">
                      <span className="ci-price-label">{t(lang, "ciPaid")}</span>
                      <span className="ci-price-val paid">{fmtMoney(paid)} THB</span>
                    </div>
                    <div className="ci-price-divider" />
                    {balance > 0 ? (
                      <>
                        <div className="ci-price-row">
                          <span className="ci-balance-label">{t(lang, "ciBalanceDue")}</span>
                          <span className="ci-balance-val due">{fmtMoney(balance)} THB</span>
                        </div>
                        <button className="ci-pay-btn" onClick={() => { setPayMethod("cash"); setShowPay(true); }}>
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="2" y="6" width="20" height="12" rx="2"/><circle cx="12" cy="12" r="3"/>
                            <path d="M6 12h.01M18 12h.01"/>
                          </svg>
                          {t(lang, "ciPay")}
                        </button>
                      </>
                    ) : (
                      <div className="ci-price-row">
                        <span className="ci-balance-label">{t(lang, "ciBalance")}</span>
                        <span className="ci-balance-val paid-full">{t(lang, "ciPaidFull")}</span>
                      </div>
                    )}
                    {payment && (
                      <div className="ci-paid-note">
                        <span>
                          {payment.method === "cash" ? t(lang, "ciReceivedCash") : t(lang, "ciReceivedQR")} {fmtMoney(payment.amount)} THB
                        </span>
                        <button className="ci-undo-btn" onClick={() => setPayment(null)}>{t(lang, "ciUndo")}</button>
                      </div>
                    )}
                  </div>

                  {/* จองห้องเดียว = ยืนยันได้เลย
                      จองหลายห้อง = ต้องไล่กรอกให้ครบทุกห้องก่อน ปุ่มยืนยันจึงโผล่ที่ห้องสุดท้าย
                      (ยืนยันครั้งเดียวเช็คอินให้ทั้งกลุ่ม) */}
                  {group.length > 1 ? (
                    <div className="ci-nav-row">
                      <button
                        className="ci-nav-btn"
                        onClick={() => goToRoom(gIdx - 1)}
                        disabled={gIdx === 0 || submitting}
                      >
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M19 12H5"/><path d="M12 19l-7-7 7-7"/>
                        </svg>
                        {t(lang, "ciPrevRoom")}
                      </button>

                      {gIdx < group.length - 1 ? (
                        <button className="ci-nav-btn next" onClick={() => goToRoom(gIdx + 1)} disabled={submitting}>
                          {t(lang, "ciNextRoom")}
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M5 12h14"/><path d="M12 5l7 7-7 7"/>
                          </svg>
                        </button>
                      ) : (
                        <button className="ci-confirm-btn group" onClick={handleCheckIn} disabled={submitting}>
                          {submitting ? t(lang, "ciConfirming") : t(lang, "ciConfirmAll", group.length)}
                        </button>
                      )}
                    </div>
                  ) : (
                    <button className="ci-confirm-btn" onClick={handleCheckIn} disabled={submitting}>
                      {submitting ? t(lang, "ciConfirming") : t(lang, "ciConfirmBtn")}
                    </button>
                  )}
                </div>

              </div>
            </div>

          ) : (
            <>
              <div className="ci-header">
                <div className="ci-title"><span>🛎️</span> {t(lang, "ciTitle")}</div>
                <div style={{ display:"flex", alignItems:"center", gap:12, flexWrap:"wrap" }}>
                  <div className="ci-tabs">
                    <button className={`ci-tab${viewTab === "all" ? " active" : ""}`} onClick={() => setViewTab("all")}>{t(lang, "ciTabAll")}</button>
                    <button className={`ci-tab${viewTab === "today" ? " active" : ""}`} onClick={() => setViewTab("today")}>
                      {t(lang, "ciTabToday")}{todayCount > 0 ? ` (${todayCount})` : ""}
                    </button>
                  </div>
                  <div className="ci-search-wrap">
                    <svg className="ci-search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                    </svg>
                    <input className="ci-search" placeholder={t(lang, "ciSearchPh")} value={search}
                      onChange={e => setSearch(e.target.value)} />
                  </div>
                </div>
              </div>

              {errorMsg && (
                <div style={{ background:"#fef2f2", border:"1px solid #fecaca", borderRadius:10, padding:"12px 16px", marginBottom:16, color:"#991b1b", fontSize:13 }}>
                  <strong>{t(lang, "ciLoadFail")}</strong> {errorMsg}
                </div>
              )}

              {loading ? (
                <div className="ci-loading"><div className="ci-spinner"/>{t(lang, "ciLoadingData")}</div>
              ) : filtered.length === 0 ? (
                <div className="ci-empty">
                  <div className="ci-empty-icon">🛎️</div>
                  <div className="ci-empty-text">{t(lang, "ciNoList")}</div>
                </div>
              ) : (
                <div className="ci-list">
                  {filtered.map(g => {
                    const b     = g[0];
                    const name  = getGuestName(b);
                    const src   = toAvatarSrc(b.user?.avatar);
                    const rooms = g.map(x => x.roomUnit?.roomNumber ?? "—").join(", ");
                    return (
                      <div className="ci-card" key={b._id}>
                        <AvatarWithFallback
                          src={src}
                          role={getAvatarRole(b.user)}
                          imgClass="ci-avatar"
                          phClass="ci-avatar-placeholder"
                        />
                        <div className="ci-info">
                          <div className="ci-name">
                            {name}
                            {g.length > 1 && (
                              <span className="ci-group-badge">🏨 {t(lang, "ciGroupRooms", g.length)}</span>
                            )}
                          </div>
                          <div className="ci-meta">
                            <div className="ci-meta-item">
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
                                <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                              </svg>
                              {t(lang, "ciRoomFor")} : {b.roomType?.adults ?? 1} {t(lang, "ciPersons")}
                            </div>
                            <div className="ci-meta-item">
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/>
                                <line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
                              </svg>
                              {t(lang, "ciBookedOn")} : {fmtDateTime(b.createdAt)}
                            </div>
                            <div className="ci-meta-item">
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M5 12h14"/><path d="M12 5l7 7-7 7"/>
                              </svg>
                              {t(lang, "ciCheckIn")} : {fmtDate(b.checkIn)}
                            </div>
                            <div className="ci-meta-item">
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M19 12H5"/><path d="M12 19l-7-7 7-7"/>
                              </svg>
                              {t(lang, "ciCheckOut")} : {fmtDate(b.checkOut)}
                            </div>
                          </div>
                        </div>
                        <div className="ci-right">
                          <div className="ci-room-block">
                            <span className="ci-room-label">{t(lang, "roomNoLabel")}</span>
                            <span className="ci-room-num">{rooms}</span>
                          </div>
                          <button className="ci-btn-checkin" onClick={() => openForm(g)}>{t(lang, "ciCheckinBtn")}</button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}

        </div>
      </div>

      {/* Payment popup */}
      {showPay && selected && (
        <div className="ci-pay-overlay" onClick={e => { if (e.target === e.currentTarget) setShowPay(false); }}>
          <div className="ci-pay-modal">
            <div className="ci-pay-title">{t(lang, "ciPayTitle")}</div>
            <div className="ci-pay-sub">{getGuestName(selected)} · {t(lang,"ciRoomLabel")} {selected.roomUnit?.roomNumber ?? "—"}</div>

            {/* ยอดเงิน (ล็อค แก้ไขไม่ได้) */}
            <div className="ci-pay-amount-box">
              <div className="ci-pay-amount-label">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                </svg>
                {t(lang, "ciAmountDue")}
              </div>
              <div className="ci-pay-amount-val">{fmtMoney(balance)} THB</div>
            </div>

            {/* เลือกวิธีชำระ */}
            <div className="ci-pay-methods">
              <button className={`ci-pay-method${payMethod === "cash" ? " active" : ""}`} onClick={() => setPayMethod("cash")}>
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="6" width="20" height="12" rx="2"/><circle cx="12" cy="12" r="3"/>
                  <path d="M6 12h.01M18 12h.01"/>
                </svg>
                {t(lang, "ciCash")}
              </button>
              <button className={`ci-pay-method${payMethod === "qr" ? " active" : ""}`} onClick={() => setPayMethod("qr")}>
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/>
                  <rect x="3" y="14" width="7" height="7" rx="1"/>
                  <path d="M14 14h3v3h-3zM21 14v.01M14 21v.01M21 21v.01M17.5 17.5v.01"/>
                </svg>
                QR Code
              </button>
            </div>

            {/* PromptPay QR — ยอดเงินล็อคใน QR */}
            {payMethod === "qr" && (
              <div className="ci-pay-qrbox">
                <div style={{ background:"#fff", padding:10, borderRadius:14, border:"1px solid #e5e7eb" }}>
                  <PromptPayQR amount={balance} size={180} />
                </div>
                <div style={{ fontWeight:600, color:"#1a1a1a", fontSize:13 }}>
                  {t(lang,"ciScanPay")} {fmtMoney(balance)} THB
                </div>
                <div style={{ textAlign:"center", lineHeight:1.6 }}>
                  {promptpayConfig.accountName} · {promptpayConfig.bankName}<br/>
                  PromptPay {promptpayConfig.phoneNumber}
                </div>
              </div>
            )}

            <div className="ci-pay-actions">
              <button className="ci-pay-cancel" onClick={() => setShowPay(false)}>{t(lang, "ciCancelBtn")}</button>
              <button className="ci-pay-ok" onClick={() => {
                setPayment({ method: payMethod, amount: balance });
                setShowPay(false);
                toast(`${payMethod === "cash" ? t(lang, "ciReceivedCash") : t(lang, "ciReceivedQR")} ${fmtMoney(balance)} THB ✓`);
              }}>
                {t(lang, "ciOk")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Walk-in booking modal */}
      {showWalkIn && (
        <div className="wi-overlay" onClick={e => { if (e.target === e.currentTarget && !wiBusy) setShowWalkIn(false); }}>
          <div className="wi-modal">
            <div className="wi-head">
              <div>
                <div className="wi-title"><span>🧾</span> {t(lang, "wiTitle")}</div>
                <div className="wi-sub">{t(lang, "wiSub")}</div>
              </div>
              <button className="wi-close" onClick={() => !wiBusy && setShowWalkIn(false)}>✕</button>
            </div>

            {wiErr && <div className="wi-err">{wiErr}</div>}

            <div className="wi-field">
              <label>{t(lang, "wiRoomType")}</label>
              <select value={wi.roomTypeId} onChange={e => setWi(w => ({ ...w, roomTypeId: e.target.value }))}>
                <option value="">{t(lang, "wiRoomTypePh")}</option>
                {roomTypes.map(r => (
                  <option key={r._id} value={r._id}>{r.name} — {Number(r.price || 0).toLocaleString()} THB</option>
                ))}
              </select>
            </div>

            <div className="wi-row">
              <div className="wi-field">
                <label>{t(lang, "wiCheckIn")}</label>
                <input type="date" value={wi.checkIn} onChange={e => setWi(w => ({ ...w, checkIn: e.target.value }))} />
              </div>
              <div className="wi-field">
                <label>{t(lang, "wiCheckOut")}</label>
                <input type="date" value={wi.checkOut} min={wi.checkIn} onChange={e => setWi(w => ({ ...w, checkOut: e.target.value }))} />
              </div>
            </div>

            <div className="wi-row">
              <div className="wi-field">
                <label>{t(lang, "wiGuestName")}</label>
                <input placeholder={t(lang, "wiGuestNamePh")} value={wi.guestName} onChange={e => setWi(w => ({ ...w, guestName: e.target.value }))} />
              </div>
              <div className="wi-field">
                <label>{t(lang, "wiPhone")}</label>
                <input placeholder={t(lang, "wiPhonePh")} value={wi.phone} onChange={e => setWi(w => ({ ...w, phone: e.target.value }))} />
              </div>
            </div>

            <div className="wi-total-box">
              <div>
                <div className="lbl">{t(lang, "wiTotal")}</div>
                <div className="nights">{wiNights} {t(lang, "wiNights")}{wiRoom ? ` × ${Number(wiRoom.price || 0).toLocaleString()}` : ""}</div>
              </div>
              <div className="val">{wiTotal.toLocaleString()} THB</div>
            </div>

            <div className="wi-field">
              <label>{t(lang, "wiPaidAmount")}</label>
              <div className="wi-seg">
                <button className={wi.paid === "full" ? "active" : ""} onClick={() => setWi(w => ({ ...w, paid: "full" }))}>{t(lang, "wiPayFull")}</button>
                <button className={wi.paid === "none" ? "active" : ""} onClick={() => setWi(w => ({ ...w, paid: "none" }))}>{t(lang, "wiPayNone")}</button>
              </div>
            </div>

            {wi.paid === "full" && (
              <div className="wi-field">
                <label>{t(lang, "wiPayMethod")}</label>
                <div className="wi-seg">
                  <button className={wi.payMethod === "cash" ? "active" : ""} onClick={() => setWi(w => ({ ...w, payMethod: "cash" }))}>{t(lang, "wiCash")}</button>
                  <button className={wi.payMethod === "qr" ? "active" : ""} onClick={() => setWi(w => ({ ...w, payMethod: "qr" }))}>QR Code</button>
                </div>
              </div>
            )}

            <div className="wi-actions">
              <button className="wi-btn-cancel" onClick={() => !wiBusy && setShowWalkIn(false)}>{t(lang, "wiCancelBtn")}</button>
              <button className="wi-btn-ok" onClick={submitWalkIn} disabled={wiBusy}>
                {wiBusy ? t(lang, "wiSubmitting") : t(lang, "wiSubmit")}
              </button>
            </div>
          </div>
        </div>
      )}

      <Toast show={!!toasting} type={toasting?.type} message={toasting?.msg} lang={lang} />
    </>
  );
}

export default CheckIn;